/**
 * MATERIAL switch — paper ⇄ canvas, without reloading the page.
 *
 * Canvas is a separate Shopify product, so the obvious implementation (the one
 * the sibling brand uses) is a link to the twin product. That reloads the page,
 * which on this tool is unacceptable: the customer is mid-design with an
 * uploaded photo and a generated portrait, and a reload drops them back to the
 * first tab with the work apparently gone.
 *
 * Everything that differs between the two materials is rendered up front —
 * window.ppMaterialData carries both products' variant ids and prices, and the
 * frame rail contains both materials' swatches tagged with data-material — so
 * the switch is pure DOM work with no network call.
 *
 * What changes on switch:
 *   - the hidden variant id the add-to-cart form posts
 *   - the price, via the existing price handler
 *   - which frame swatches are shown (canvas takes float frames)
 *   - the canvas weave on the preview
 *   - the material line under the poster
 *   - the size options (canvas exists only in the sizes the printer stocks)
 *   - the URL, via replaceState, so a refresh or a share keeps the material
 */
(function () {
  var data = window.ppMaterialData;
  if (!data || !data.paper || !data.canvas) return;

  var links = document.querySelector('.pp-material-links');
  if (!links) return;

  // Sizes that exist on canvas. Derived from the canvas product itself rather
  // than hardcoded, so adding a canvas size in Shopify is enough.
  var canvasSizes = Object.keys(data.canvas.variants || {});

  // pp-money.js watches [data-cents] and rewrites the text from the ATTRIBUTE,
  // so setting textContent alone is undone on the next mutation. Follow its
  // contract: set data-cents and let it format, which also keeps the price
  // locale-correct in every market currency.
  function setPrice(el, cents, fallbackText) {
    if (!el) return;
    el.dataset.cents = String(cents);
    el.textContent = (typeof window.ppFormatMoney === 'function')
      ? window.ppFormatMoney(cents)
      : (fallbackText || el.textContent);
  }

  function currentSize() {
    var checked = document.querySelector('.bp-variant-options-radio:checked');
    return checked ? (checked.value || checked.getAttribute('data-value')) : null;
  }

  // ── Query params the customizer reads, and the theme happily throws away ──
  // A style page arrives as ?illustration=…&heading=…, and switching material
  // makes the theme's variant handler rewrite the URL to a bare ?variant=…,
  // taking those with it. The customizer keys off ?illustration= (see
  // pp-ai-portrait-handler-v2), so after a switch a reload dropped the customer
  // back on the default sketch with their chosen style gone.
  //
  // The theme rewrites asynchronously, after this function has finished, so
  // writing our own URL last does not win the race. Instead we re-attach the
  // params to whatever URL anything writes for a moment either side of the
  // switch, then put history back exactly as we found it.
  // `style` matters beyond the look: it selects the staff-edited generation
  // prompt, and it is what per-style reporting is keyed on.
  var KEEP = ['illustration', 'display', 'style', 'illustration_name',
              'heading', 'subheading', 'duo', 'edit_token', 'approve_mode'];

  function keepParams(url) {
    try {
      var next = new URL(url, location.origin);
      var here = new URLSearchParams(location.search);
      KEEP.forEach(function (k) {
        if (here.has(k) && !next.searchParams.has(k)) next.searchParams.set(k, here.get(k));
      });
      return next.pathname + next.search + next.hash;
    } catch (e) { return url; }
  }

  var holding = 0;
  function holdParams() {
    if (holding) { clearTimeout(holding); }
    else {
      var realReplace = history.replaceState.bind(history);
      var realPush = history.pushState.bind(history);
      history.replaceState = function (s, t, u) { return realReplace(s, t, u == null ? u : keepParams(u)); };
      history.pushState = function (s, t, u) { return realPush(s, t, u == null ? u : keepParams(u)); };
      holdParams.release = function () {
        history.replaceState = realReplace;
        history.pushState = realPush;
        holding = 0;
      };
    }
    holding = setTimeout(function () { holdParams.release(); }, 2000);
  }

  // applyMaterial dispatches a size change so the price handler recomputes, and
  // the size listener below calls applyMaterial — without this guard the two
  // bounce off each other forever.
  var applying = false;

  function applyMaterial(material, opts) {
    if (applying) return;
    applying = true;
    try {
      applyMaterialInner(material, opts);
    } finally {
      applying = false;
    }
  }

  function applyMaterialInner(material, opts) {
    opts = opts || {};
    var isCanvas = material === 'canvas';
    var info = data[material];
    if (!info) return;

    // 1. Buttons
    links.querySelectorAll('.pp-material-link').forEach(function (b) {
      b.classList.toggle('active', b.getAttribute('data-material') === material);
    });

    // 2. Preview texture. The CSS reads --canvas-bg-texture, which Liquid only
    // inlines when the page was rendered by the canvas product, so set the
    // custom properties here too — otherwise the classes land with nothing
    // behind them and the poster still looks like paper.
    var tpl = document.querySelector('.petposter-template');
    if (tpl) {
      tpl.classList.toggle('canvas-poster', isCanvas);
      tpl.classList.toggle('style-texture', isCanvas);
      var tex = (data.canvas && data.canvas.textures) || null;
      if (isCanvas && tex) {
        if (tex.weave) tpl.style.setProperty('--pp-canvas-weave', 'url(' + tex.weave + ')');
        if (tex.weaveLight) tpl.style.setProperty('--pp-canvas-weave-light', 'url(' + tex.weaveLight + ')');
        if (tex.wood) tpl.style.setProperty('--pp-wood-grain', 'url(' + tex.wood + ')');
        tpl.style.setProperty('--canvas-bg-texture', 'url(' + tex.portrait + ')');
        tpl.style.setProperty('--canvas-bg-texture-horizontal', 'url(' + tex.horizontal + ')');
        tpl.style.setProperty('--canvas-bg-texture-horizontal-70x100', 'url(' + tex.horizontal70100 + ')');
      } else if (!isCanvas) {
        tpl.style.removeProperty('--pp-canvas-weave');
        tpl.style.removeProperty('--pp-canvas-weave-light');
        tpl.style.removeProperty('--pp-wood-grain');
        tpl.style.removeProperty('--canvas-bg-texture');
        tpl.style.removeProperty('--canvas-bg-texture-horizontal');
        tpl.style.removeProperty('--canvas-bg-texture-horizontal-70x100');
      }
    }

    // 3. Material line under the poster
    var bottom = document.querySelector('.bp-poster-bottom-material');
    if (bottom && info.bottomText) bottom.textContent = info.bottomText;

    // 4. Frame swatches for this material. Any frame already chosen belongs to
    // the other material, so it has to be replaced rather than carried across —
    // with NO FRAME, which both materials sell. The NO FRAME swatch carries no
    // data-material, so the loop below leaves it visible for both and a
    // customer who had no frame keeps none across the switch.
    var rails = document.querySelectorAll('.bp-frame-swatch-container[data-material]');
    if (rails.length) {
      rails.forEach(function (el) {
        el.style.display = el.getAttribute('data-material') === material ? '' : 'none';
      });
      var chosen = document.querySelector('.bp-frame-radio:checked');
      var chosenRail = chosen && chosen.closest('.bp-frame-swatch-container[data-material]');
      var mine = !!(chosenRail && chosenRail.getAttribute('data-material') === material);
      // Canvas opens on the light oak float frame (Aleks, 2026-10-01). A bare
      // 39mm bar is what a canvas looks like before it is finished, and the
      // frame is most of what makes it read as the premium thing it is sold
      // as. Paper still falls back to NO FRAME, which is what it sells most of,
      // and either way the customer can set it to anything including none.
      var next = material === 'canvas'
        ? document.querySelector('.bp-frame-swatch-container[data-material="canvas"] ' +
                                 '.bp-frame-radio[data-image*="frame-oak-canvas"]')
        : null;
      next = next || document.getElementById('frame-opt-0');
      if (!mine && next && next !== chosen) {
        next.checked = true;
        next.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 4b. The frame info modal describes a different product per material.
    document.querySelectorAll('.bp-frame-modal-set[data-material]').forEach(function (el) {
      el.style.display = el.getAttribute('data-material') === material ? '' : 'none';
    });

    // 5. Sizes. The visible picker is .bp-size-card-inline, not the radio group
    // (which is hidden form state). Only the cards the theme already shows are
    // touched, so the sizes it deliberately hides stay hidden. A digital file
    // has no material, and canvas exists only in the sizes the printer stocks.
    var size = currentSize();
    var cards = document.querySelectorAll('.bp-size-card-inline');
    cards.forEach(function (card) {
      var title = card.getAttribute('data-bp-value') || '';
      var available = !isCanvas || canvasSizes.indexOf(title) !== -1;
      card.style.display = available ? '' : 'none';
      // Card prices are per material too.
      var v = info.variants[title];
      if (v) setPrice(card.querySelector('.bp-size-card-inline-price'), v.price, v.priceFormatted);
    });
    if (isCanvas && size && canvasSizes.indexOf(size) === -1) {
      size = canvasSizes[0];
      var target = [].slice.call(cards).filter(function (c) { return c.getAttribute('data-bp-value') === size; })[0];
      if (target) target.click();
    }

    // 6. The variant the form posts. Set LAST: the theme's own size-change
    // handler rewrites this input from the paper product's variant map, so
    // setting it before the events below meant the canvas id was overwritten
    // and a canvas add-to-cart quietly added a paper poster.
    var variant = info.variants[size] || info.variants[Object.keys(info.variants)[0]];

    // 7. Price. pp-input-handler owns the total and reads poster prices from
    // window.variantPrices, so point that at this material's prices and let the
    // existing handler recompute — writing the number here would be overwritten
    // by the next frame or size change anyway.
    var prices = {};
    Object.keys(info.variants).forEach(function (title) {
      prices[title] = { price: info.variants[title].price, formatted: info.variants[title].priceFormatted };
    });
    window.variantPrices = prices;

    document.dispatchEvent(new CustomEvent('pp:material-changed', { detail: { material: material, variantId: variant && variant.id } }));

    // Only on a real switch. These synthetic events make the theme's variant
    // handler rewrite the URL to ?variant=..., which strips the ?illustration=
    // param a style page redirects in with — and pp-ai-portrait-handler-v2
    // guards on exactly that param before restoring the last saved artwork.
    // Firing them on load therefore made every style page restore the previous
    // portrait instead of showing that style's sketch. The server has already
    // rendered the correct prices on load, so there is nothing to recompute.
    if (!opts.silent) {
      holdParams();
      var sizeRadio = document.querySelector('.bp-variant-options-radio:checked');
      if (sizeRadio) sizeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      var frameRadio = document.querySelector('.bp-frame-radio:checked');
      if (frameRadio) frameRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var idInput = document.querySelector('input.product-variant-id[name="id"]');
    if (idInput && variant) idInput.value = String(variant.id);

    // Card prices, again: the theme rewrites them while handling the size
    // change above, from its own server-rendered figures, so the canvas prices
    // have to be re-applied after it has finished.
    cards.forEach(function (card) {
      var v = info.variants[card.getAttribute('data-bp-value') || ''];
      if (v) setPrice(card.querySelector('.bp-size-card-inline-price'), v.price, v.priceFormatted);
    });

    data.current = material;

    // 8. Keep the URL honest without navigating, so a refresh or a shared link
    // lands on the same material.
    if (!opts.silent) {
      var url = links.getAttribute(isCanvas ? 'data-canvas-url' : 'data-paper-url');
      if (url) {
        try { history.replaceState({ ppMaterial: material }, '', keepParams(url)); } catch (e) {}
      }
    }
  }

  // ── The design each material starts from ────────────────────────────────
  // A canvas carrying a poster design — white margins round the artwork —
  // photographs as a poster in a frame, which is not what anyone buys a canvas
  // for. Picking CANVAS therefore lands on the way a canvas is meant to look:
  // the artwork filling the piece, edge to edge. Picking POSTER lands back on
  // the poster default, or we would have the same problem in reverse.
  //
  // The name and the line under it come OFF (Aleks, 2026-10-02, reversing the
  // day before). Text over the artwork is a poster's idea; a canvas is bought
  // as a picture.
  //
  // A starting point, not a lock: every one of these is still a control the
  // customer can set, and typing a heading switches its toggle back on by
  // itself.
  var PRESETS = {
    canvas: { noHeading: true, noSubheading: true, fillPoster: true },
    paper: { noHeading: false, noSubheading: false, fillPoster: false },
  };

  // Other scripts remember these three across pages. A preset is our choice,
  // not the customer's, so it must not overwrite what they last chose
  // themselves — otherwise a look at canvas leaves every later paper poster
  // with no text on it.
  var PRESET_KEYS = ['pp.noHeading', 'properties[__no_heading]',
                     'properties[__no_subheading]', 'properties[__FillPoster]'];

  function setChecked(el, on) {
    if (!el || el.checked === on) return;
    el.checked = on;
    // Native, not $.trigger: some of these are bound with addEventListener,
    // which jQuery's synthetic trigger never reaches.
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function applyDesignPreset(material) {
    var p = PRESETS[material];
    if (!p) return;
    var saved = {};
    try { PRESET_KEYS.forEach(function (k) { saved[k] = localStorage.getItem(k); }); } catch (e) {}
    setChecked(document.getElementById('bp-no-heading-checkbox-1'), p.noHeading);
    setChecked(document.getElementById('bp-no-subheading-checkbox-1'), p.noSubheading);
    setChecked(document.getElementById('bp-fill-poster-toggle'), p.fillPoster);
    function restore() {
      try {
        PRESET_KEYS.forEach(function (k) {
          if (saved[k] === null) localStorage.removeItem(k);
          else localStorage.setItem(k, saved[k]);
        });
      } catch (e) {}
    }
    restore();
    setTimeout(restore, 0);
  }

  // Canvas changes the object, not the design: a stretched, glassless piece
  // rather than a sheet of paper. Say so once, the first time it is picked —
  // after that the customer knows, and a modal on every switch is a nuisance.
  // A browser that blocks storage simply sees it each time rather than never.
  var CANVAS_INTRO_KEY = 'pp.canvasIntroSeen';
  function maybeShowCanvasIntro() {
    var modal = document.getElementById('pp-canvas-intro-modal');
    if (!modal) return;
    var seen = false;
    try { seen = localStorage.getItem(CANVAS_INTRO_KEY) === '1'; } catch (e) {}
    if (seen) return;
    try { localStorage.setItem(CANVAS_INTRO_KEY, '1'); } catch (e) {}
    modal.classList.add('active');
  }

  links.addEventListener('click', function (e) {
    var btn = e.target.closest('.pp-material-link');
    if (!btn) return;
    e.preventDefault();
    var material = btn.getAttribute('data-material');
    if (material && material !== data.current) {
      applyMaterial(material);
      // After applyMaterial: the preset touches the same preview that the
      // material change rebuilds, and the later write has to be the preset's.
      applyDesignPreset(material);
      if (material === 'canvas') maybeShowCanvasIntro();
    }
  });

  // Re-apply on size change: the variant id must follow the material.
  document.addEventListener('change', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('bp-variant-options-radio')) {
      applyMaterial(data.current, { silent: true });
    }
  });

  // Which material this page IS, decided from the product handle rather than
  // the Liquid flag. That flag comes from collection membership, and a render
  // cached before the canvas collection was published says "paper" on the
  // canvas product's own page — which showed paper prices and paper frames on
  // a canvas. The handle cannot be stale.
  var hereHandle = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
                    window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.handle) || '';
  if (!hereHandle) {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    hereHandle = m ? m[1] : '';
  }
  if (hereHandle === data.canvas.handle) data.current = 'canvas';
  else if (hereHandle === data.paper.handle) data.current = 'paper';

  applyMaterial(data.current, { silent: true });

  window.ppSetMaterial = applyMaterial;
})();
